/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import type {
    PortalContext,
    SCompanyChangeEvent,
    SCompanyIdResolveFailedEvent,
} from "@avalara/skylab-sdk";
import {
    AuiFooter,
    AuiHeader,
    AuiLanguageSupportDialog,
    AuiSessionTimeout,
    AuiSidenav,
    AuiSupernav,
    SLoader,
    SToasts,
} from "@avalara/skylab-sdk/react";
import debounce from "lodash/debounce";
import React, {
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { getCertificateStatusesAsync } from "../../app/certificateSlice";
import { fetchDataServiceFeatureFlags } from "../../app/dataServiceFeatureFlagsSlice";
import {
    fetchAllPreferences,
    saveLocalePreference,
} from "../../app/preferencesSlice/preferencesActions";
import { fetchRetailSettings } from "../../app/retailSettingsSlice";
import {
    getSessionAsync,
    refreshSessionContextAsync,
    selectIsCompanyProvisioned,
    selectSessionUIFields,
    selectSkylabHeader,
} from "../../app/sessionSlice";
import type { SessionState } from "../../app/sessionSlice/types";
import { fetchSettingsAPI, setPreviousPath } from "../../app/settingsSlice";
import { AppDispatch } from "../../app/types";
import getConfig from "../../config";
import featureFlag from "../../featureToggler/featureFlag";
import "../../globalTypes";
import { useIsNoAccessUser } from "../../hooks/useAccessControl";
import { useDismissToastsOnNavigate } from "../../hooks/useDismissToastsOnNavigate";
import { useIsAuthorized } from "../../hooks/useIsAuthorized";
import { useTokenProvider, type TokenProviderFunction } from "../../hooks/useTokenProvider";
import { useLocale } from "../../i18n/useLocale";
import { useLocaleOptions } from "../../i18n/useLocaleOptions";
import isOneWebDomainRuntime from "../../shared/avaAppRuntime";
import { isAviatorCertificateRoute, isDedicatedAviatorPage } from "../../shared/aviatorIframe";
import {
    AVA_APP_NAME,
    ERROR_PAGE_TYPES,
    PREVIOUS_ACCOUNT_ID_COOKIE_NAME,
    SessionRefreshState,
    SessionRefreshStateType,
} from "../../shared/constants";
import { watchAviatorShellPopups } from "../../shared/dismissAviatorShellPopups";
import { cleanupSessionRefreshTimeout } from "../../shared/sessionTimeoutCleanup";
import { getPageTitle } from "../../shared/sessionUtility";
import {
    clearAccountSwitchStorageKeys,
    clearLocalStorage,
    getCookie,
    retainCriticalData,
    setCookie,
    urlToPageMap,
} from "../../shared/Utils";
import zoominWidget from "../../shared/zoominWidget";
import ErrorBoundaryWrapper from "../errorHandling/errorBoundary";
import ErrorPage from "../errorHandling/ErrorPage";
import { useManagedServiceContextSync } from "../managedServices/components/useManagedServiceContextSync";
import Navigation from "../navigation/Navigation";
import { NavigationVisibilityContext } from "../navigation/NavigationVisibilityProvider";
import { NavigationVisibilityContextType } from "../navigation/types";
import Provision from "../provision/Provision";
import { useLocaleSwitcherFeatureFlag } from "../sharedComponents/useLocaleSwitcherFeatureFlag";
import { useSideNavFeatureFlag } from "../sharedComponents/useSideNavFeatureFlag";
import { useThemeSwitcherFeatureFlag } from "../sharedComponents/useThemeSwitcherFeatureFlag";
import { useWaffleMenuFeatureFlag } from "../sharedComponents/useWaffleMenuFeatureFlag";
import AppVersion from "./AppVersion";
import { useActiveNavSync } from "./useActiveNavSync";
import { useClearTaxTypeDetailsOnCompanyChange } from "./useClearTaxTypeDetailsOnCompanyChange";
import { usePrefetchTaxTypeDetails } from "./usePrefetchTaxTypeDetails";

// Config is retrieved lazily inside functions to support runtime configuration
const getLazyConfig = () => {
    const config = getConfig();
    return {
        appEnv: config.appEnv,
        ecmApiHost: config.hostnames.ecmApiHost,
        identity: config.hostnames.identity,
    };
};

const getMainContentClassName = (isRetailView: boolean, hideNav: boolean): string => {
    if (isRetailView || !hideNav) {
        return "dl-content flex dl-flex-fill-height";
    }
    return "flex dl-flex-fill-height";
};

// Strips the AvaApp router basename (e.g. "/exemptions") from a pathname, if present.
// In AvaApp the router basename is "/exemptions", so it must be stripped before storing
// or navigating — otherwise navigate() re-prepends it, duplicating the basename in the URL,
// or the router fails to match the route.
const stripAvaAppBasename = (pathname: string, avaAppBasename: string): string =>
    pathname.startsWith(avaAppBasename) ? pathname.slice(avaAppBasename.length) || "/" : pathname;

// (Only for top nav) The menu dropdown doesn't close automatically on selecting an item, so we
// have to programmatically click the expanded menu button to close it.
const closeExpandedNavMenu = (currentNav: HTMLElement | null): void => {
    if (!currentNav) return;
    Array.from(currentNav.getElementsByTagName("button"))
        .find((button: HTMLButtonElement) => button.getAttribute("aria-expanded") === "true")
        ?.click();
};

// Helper function to determine app DOM content
const getAppDOMContent = (
    session: Pick<SessionState, "accountId" | "provisionStatus">,
    isCompanyProvisioned: boolean,
    isNoAccessUserFlag: boolean,
    currentLocation: string,
    isSideNavEnabled: boolean,
    isLoading: boolean,
    hasInitialSessionLoaded: boolean,
    hasSessionLoadError: boolean
): React.JSX.Element | null => {
    if (hasSessionLoadError) {
        return <ErrorPage type={ERROR_PAGE_TYPES.INTERNAL_SERVER_ERROR} />;
    }

    if (isLoading || !hasInitialSessionLoaded) {
        return (
            <div className="loader-container">
                <h1>Loading ...</h1>
                <SLoader id="page-loader" className="large" aria-live="polite" loading />
            </div>
        );
    }

    if (
        !isLoading &&
        ((session.accountId && (isCompanyProvisioned || isNoAccessUserFlag)) ||
            currentLocation.includes("health-check"))
    ) {
        return <Navigation isSideNavEnabled={isSideNavEnabled} />;
    }

    if (session.accountId) {
        return (
            <div className={`margin-top-lg provision-container`}>
                <Provision />
            </div>
        );
    }

    return null;
};

function Application(): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const session = useSelector(selectSessionUIFields, shallowEqual) as Partial<SessionState>;
    const isCompanyProvisioned = useSelector(selectIsCompanyProvisioned);

    const header = useSelector(selectSkylabHeader, shallowEqual);

    const { hideNav }: NavigationVisibilityContextType = useContext(NavigationVisibilityContext);
    const isNoAccessUserFlag = useIsNoAccessUser();
    const currentLocation: string = window.location.href;
    const isAviatorCertificateView =
        isAviatorCertificateRoute(location.pathname, location.search) ||
        isDedicatedAviatorPage(location.pathname);
    const isRetailView =
        !currentLocation.includes(urlToPageMap.settings) &&
        currentLocation.includes(urlToPageMap.retail);
    const sideNavRef = useRef<React.ElementRef<typeof AuiSidenav>>(null);
    const superNavRef = useRef<React.ElementRef<typeof AuiSupernav>>(null);

    const shouldSelectSettingsInSupernav = useIsAuthorized(
        featureFlag.topNav.activateSettingsTab,
        true
    );
    const errorBoundaryRef = useRef<{ removeError?: () => void }>();
    const zoomInWidgetId17 = useIsAuthorized(featureFlag.zoomInWidget.id17, true);
    const zoomInWidgetId18 = useIsAuthorized(featureFlag.zoomInWidget.id18, true);
    const zoomInWidgetId23 = useIsAuthorized(featureFlag.zoomInWidget.id23, true);
    const canAccessRetail = useIsAuthorized(featureFlag.retail.index, true);
    const isSideNavEnabled = useSideNavFeatureFlag();
    const isWaffleMenuEnabled = useWaffleMenuFeatureFlag();
    const isLocaleSwitcherEnabled = useLocaleSwitcherFeatureFlag();
    const isThemeSwitcherEnabled = useThemeSwitcherFeatureFlag();
    useManagedServiceContextSync();
    useDismissToastsOnNavigate();
    const localeOptions = useLocaleOptions();
    const activeLocale = useLocale();
    // Skip the first s-localeselect event which is always an init broadcast from
    // Skylab (watchUser fires it on mount). Only subsequent events are genuine
    // user selections.
    const localeSelectInitFiredRef = useRef<boolean>(false);
    const [refreshState, setRefreshState] = useState<SessionRefreshStateType>(
        SessionRefreshState.Idle
    );
    const [hasInitialSessionLoaded, setHasInitialSessionLoaded] = useState(false);
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // In-flight guard and replay queue for `runResync` below — see its doc comment for why
    // these are refs rather than state.
    const isRefreshingRef = useRef(false);
    const pendingForceResyncRef = useRef<boolean | null>(null);
    // Accumulates `forceResync` across the *debounce* window (as opposed to
    // `pendingForceResyncRef`, which accumulates across the in-flight window) — see
    // `debouncedRunResync`'s doc comment for why this is needed.
    const pendingDebounceForceResyncRef = useRef(false);
    const isLoading: boolean =
        (session.isLoading ?? false) || refreshState === SessionRefreshState.Refreshing;

    useEffect(() => {
        document.body.classList.toggle("aviator-certificate-view", isAviatorCertificateView);

        if (!isAviatorCertificateView) {
            return () => {
                document.body.classList.remove("aviator-certificate-view");
            };
        }

        const stopWatchingShellPopups = watchAviatorShellPopups();

        return () => {
            stopWatchingShellPopups();
            document.body.classList.remove("aviator-certificate-view");
        };
    }, [isAviatorCertificateView]);

    const logout = (): void => {
        // Clean up session refresh timeout to prevent memory leaks
        cleanupSessionRefreshTimeout();

        retainCriticalData();
        clearLocalStorage();
        const { appEnv, ecmApiHost } = getLazyConfig();
        const baseURL: string =
            appEnv === "local" ? `http://${ecmApiHost}/api` : `https://${ecmApiHost}/api`;
        window.location.href = `${baseURL}/v3/auth/logout`;
    };

    useEffect(() => {
        if (!currentLocation.includes("/health-check")) dispatch(getSessionAsync());
        const documentHeader = document.querySelector("aui-header");
        if (documentHeader) {
            documentHeader.addEventListener("click", (event: Event) => {
                const target = event.target as HTMLElement;
                if (target && target.textContent === "Sign out") {
                    logout();
                }
            });
        }
    }, [dispatch, currentLocation]);

    useEffect(() => {
        if (session.isAuthenticated) {
            dispatch(fetchDataServiceFeatureFlags());
        }
    }, [dispatch, session.isAuthenticated]);

    useEffect(() => {
        if (!hasInitialSessionLoaded && session.isAuthenticated) {
            setHasInitialSessionLoaded(true);
        }
    }, [session.isAuthenticated, hasInitialSessionLoaded]);

    // Track accountId changes using cookies to detect account switch (not company switch)
    // Cookies persist across page reloads and redirects within the same domain
    useEffect(() => {
        const currentAccountId = session.accountId;

        // Skip if accountId is not yet loaded
        if (!currentAccountId) {
            return;
        }

        // Get the previously stored accountId from cookie
        const previousAccountId = getCookie(PREVIOUS_ACCOUNT_ID_COOKIE_NAME);

        // If we have a previous accountId and it's different from current, account has changed
        if (previousAccountId && previousAccountId !== currentAccountId) {
            // Account has changed - clear account-specific localStorage keys
            clearAccountSwitchStorageKeys();
        }

        // Store current accountId in cookie for next comparison (persists across reloads/redirects)
        // Set cookie to expire in 30 days (long enough to survive redirects but not permanent)
        setCookie(PREVIOUS_ACCOUNT_ID_COOKIE_NAME, currentAccountId, 30);
    }, [session.accountId]);

    useClearTaxTypeDetailsOnCompanyChange(session.activeCompany?.id);
    usePrefetchTaxTypeDetails(session.activeCompany?.id);

    useEffect(() => {
        if (session.activeCompany?.id && isCompanyProvisioned) {
            dispatch(fetchSettingsAPI(true));
            dispatch(getCertificateStatusesAsync());
            dispatch(fetchAllPreferences());
            if (canAccessRetail) {
                dispatch(fetchRetailSettings());
            }
        }
        if (session.activeCompany?.name) {
            document.title = dispatch(getPageTitle());
        }
    }, [
        dispatch,
        session.activeCompany?.id,
        session.activeCompany?.name,
        isCompanyProvisioned,
        session.subscriptions,
        canAccessRetail,
    ]);

    /**
     * Resyncs this tab's session state (SPA navigate to "/" + re-fetch session) after an ambient
     * context change — company or account — is known to have touched the shared cookies.
     *
     * `forceResync` should be `true` whenever the triggering signal is itself proof of an ambient
     * (cross-tab-visible) change — see call sites below — bypassing the `refreshSessionContextAsync`
     * result check.
     *
     * We deliberately should NOT gate the resync solely on `refreshSessionContextAsync`'s
     * `refreshed` flag: that flag only reports whether *this request's* cookies disagreed on the
     * server, which is a property of request timing, not of whether this tab's local state is stale.
     * When another tab drives the switch first, this tab's cookies can already be in sync by the
     * time this call lands, so `refreshed` comes back `false` even though this tab still needs to
     * reload its session data. `forceResync` is the correct, request-timing-independent signal for
     * that.
     *
     * Conversely, we keep `result ||` alongside `forceResync` (rather than relying on `forceResync`
     * alone) because in standalone/non-AvaApp mode there is no `portalContextChange` broadcast to
     * force a resync on — `s-change-company` with `persisted: false` is the only signal available
     * there for some tab-local company changes, and `refreshSessionContextAsync`'s own `refreshed`
     * result is what still catches a stale cookie in that case.
     *
     * Wrapped in `debounce` below to coalesce near-simultaneous events (e.g. `portalContextChange`
     * and the `s-change-company` it triggers) into a single refresh.
     *
     * The in-flight check reads `isRefreshingRef`, a ref, rather than `refreshState`, React state.
     * Three listeners (`portalContextChange`, `s-change-company`, `s-company-id-resolve-failed`)
     * now feed the same debounce instance, so this callback — and the debounce wrapping it — must
     * stay referentially stable across a resync. If `refreshState` were a dependency here, the
     * first `setRefreshState(Refreshing)` would recreate the debounce mid-flight, and the effect
     * below re-running on that new identity would `cancel()` the very instance an already-scheduled
     * follow-on event was waiting on. A follow-on event that arrives while a resync is genuinely in
     * flight (i.e. after the debounce's own window has already closed) isn't dropped either — it's
     * queued in `pendingForceResyncRef` and replayed once the current resync's `finally` runs.
     */
    const runResync = useCallback(
        (forceResync: boolean): void => {
            // A resync is already running — queue this request instead of running concurrently
            // or dropping it. `||` so a later plain resync never downgrades an earlier forced one.
            if (isRefreshingRef.current) {
                pendingForceResyncRef.current = pendingForceResyncRef.current || forceResync;
                return;
            }

            isRefreshingRef.current = true;
            setRefreshState(SessionRefreshState.Refreshing);

            refreshTimeoutRef.current = setTimeout(() => {
                setRefreshState(SessionRefreshState.Idle);
            }, 15000);

            (async () => {
                try {
                    // Still call this so the server can self-heal this tab's `aui-context`
                    // cookie on the response — and see the comment above for why its `refreshed`
                    // result is kept as a resync signal alongside `forceResync` rather than
                    // replaced by it.
                    const result = (await dispatch(refreshSessionContextAsync())) as boolean;
                    if (result || forceResync) {
                        // Use SPA navigation instead of hard reload to preserve state
                        navigate("/", { replace: true });
                        // Refresh session state after navigation
                        await dispatch(getSessionAsync(true));
                    }
                } finally {
                    if (refreshTimeoutRef.current) {
                        clearTimeout(refreshTimeoutRef.current);
                        refreshTimeoutRef.current = null;
                    }
                    setRefreshState(SessionRefreshState.Idle);
                    isRefreshingRef.current = false;

                    // Replay a queued ambient event that arrived while this resync was running,
                    // rather than dropping it.
                    const queuedForceResync = pendingForceResyncRef.current;
                    pendingForceResyncRef.current = null;
                    if (queuedForceResync !== null) {
                        runResync(queuedForceResync);
                    }
                }
            })();
        },
        [dispatch, navigate]
    );

    // `debounce` is plain last-call-wins: if a burst of events within the 300ms window disagrees
    // on `forceResync` (e.g. `portalContextChange` fires `debouncedResync(true)`, then the
    // `s-change-company` it triggers fires `debouncedResync(false)` before the timer elapses),
    // lodash would otherwise silently drop the earlier `true` and run the single coalesced
    // `runResync` call with `false`. That's the same forceResync signal `pendingForceResyncRef`
    // exists to preserve for the in-flight case — so accumulate it here too, across the debounce
    // window, rather than letting debounce's argument-coalescing throw it away.
    const debouncedRunResync = useMemo(
        () =>
            debounce(() => {
                const forceResync = pendingDebounceForceResyncRef.current;
                pendingDebounceForceResyncRef.current = false;
                runResync(forceResync);
            }, 300),
        [runResync]
    );

    const debouncedResync = useCallback(
        (forceResync: boolean): void => {
            pendingDebounceForceResyncRef.current =
                pendingDebounceForceResyncRef.current || forceResync;
            debouncedRunResync();
        },
        [debouncedRunResync]
    );

    /**
     * Handle company change events from Skylab SDK's AuiSidenav/AuiHeader components.
     *
     * Per Skylab SDK docs (https://skylabsdk.avalara.io/views/layout/sidenav/), `e.detail` is
     * `SCompanyChangeEvent`: `company`, `initialLoad`, `source`, and `persisted`.
     * - `initialLoad: true` only for the sidenav's first bootstrap resolution on page load —
     *   nothing changed, so we skip it.
     * - `persisted: false` is a tab-local `companyId`-prop change (no cookie write, no
     *   `portalContextChange` broadcast) — there's nothing shared for *this* tab to catch up on,
     *   so we don't force a resync (`debouncedResync(false)`). It can still resync in standalone/
     *   non-AvaApp mode, though: `runResync` also gates on `refreshSessionContextAsync`'s own
     *   `refreshed` result, which is the fallback signal for that case (see its doc comment).
     * - `persisted: true` covers every change that touched the shared company cookie: the
     *   switcher, `setCompanyAndUpdateCookie()`, the bootstrap cookie/default path, and —
     *   crucially — a `portal_context_change` broadcast arriving from *another tab* (i.e. an
     *   account switch elsewhere that also resolved a company for the new account). That's the
     *   case that actually needs this tab's Redux `session.activeCompany` resynced, and forces it
     *   (`debouncedResync(true)`) regardless of `refreshSessionContextAsync`'s result.
     */
    const handleCompanyChange = useCallback(
        (event: Event): void => {
            const customEvent = event as CustomEvent<SCompanyChangeEvent>;

            // Skip if initialLoad is true (sidenav initially identifying active company)
            // Also skip if event detail is missing (malformed event)
            if (!customEvent.detail || customEvent.detail.initialLoad) {
                return;
            }

            debouncedResync(customEvent.detail.persisted);
        },
        [debouncedResync]
    );

    /**
     * Handle the case where the sidenav's company resolution settles on *no* company — the only
     * signal for that outcome, since `s-change-company` cannot represent "now no company".
     *
     * We only resync here when `source: 'portal_context_change'`: that's an ambient account switch
     * (this tab's own or another tab's) that left the new account with no resolvable company, so
     * this tab's stale `session.activeCompany` needs to be cleared. The other `source` values
     * (`initial_load`, `companyid_prop_change`, `set_active_company_method`) are local resolution
     * failures with nothing ambient to catch up on.
     */
    const handleCompanyResolveFailed = useCallback(
        (event: Event): void => {
            const customEvent = event as CustomEvent<SCompanyIdResolveFailedEvent>;

            if (customEvent.detail?.source !== "portal_context_change") {
                return;
            }

            debouncedResync(true);
        },
        [debouncedResync]
    );

    /**
     * Handle the Portal Context Change Standard's `portalContextChange` window event directly —
     * fired (per the platform contract) whenever the shared account/company context actually
     * changed and was persisted, whether from this tab or broadcast in from another one.
     *
     * This event is runtime-gated, not scoped to account switches specifically:
     * - In AvaApp (OWD), the nav bar's switch callback is wired by the host shell to
     *   `window.AvaApp.setCurrentContext()`, which writes the cookie, dispatches this event, and
     *   broadcasts cross-tab — for *both* company and account switches. The sidenav then reacts to
     *   this same event by re-resolving the company and emitting `s-change-company` itself, which
     *   is why `debouncedResync` below is debounced (see its doc comment).
     * - In standalone (non-AvaApp) mode there is no `window.AvaApp`, so the plain nav bar
     *   dropdown never fires this event at all — only `s-change-company`/`s-company-id-resolve-failed`
     *   do, and those are already handled below. The only standalone path that still dispatches it
     *   is the Managed Services switch flow (`publishPortalContext()` in
     *   `features/managedServices/services/portalContext.ts`), which fires it manually.
     *
     * Subscribing to this directly (rather than relying solely on `s-change-company`) is what
     * makes an AvaApp-driven switch reliably refresh this tab even when the sidenav's company
     * resolution for the new account is slow, fails, or (in principle) happens to settle on the
     * same company id — none of which would otherwise emit a distinguishable `s-change-company`.
     * We never call `setCurrentContext()` here, so this cannot create a feedback loop.
     */
    const handlePortalContextChange = useCallback(
        (event: Event): void => {
            const customEvent = event as CustomEvent<PortalContext>;

            if (!customEvent.detail) {
                return;
            }

            debouncedResync(true);
        },
        [debouncedResync]
    );

    useEffect(() => {
        document.addEventListener("s-change-company", handleCompanyChange);
        document.addEventListener("s-company-id-resolve-failed", handleCompanyResolveFailed);
        window.addEventListener("portalContextChange", handlePortalContextChange);

        return () => {
            document.removeEventListener("s-change-company", handleCompanyChange);
            document.removeEventListener("s-company-id-resolve-failed", handleCompanyResolveFailed);
            window.removeEventListener("portalContextChange", handlePortalContextChange);
            debouncedRunResync.cancel();
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [
        handleCompanyChange,
        handleCompanyResolveFailed,
        handlePortalContextChange,
        debouncedRunResync,
    ]);

    useEffect(() => {
        const handleLocaleSelect = (event: Event): void => {
            const customEvent = event as CustomEvent<{ locale: string }>;
            const locale: string = customEvent.detail?.locale;
            if (!locale) return;

            // Skylab's aui-locale-switcher fires s-localeselect once on mount via
            // watchUser() to broadcast its initially resolved locale. Skip that
            // first event — it reflects the identity-token locale, not the ECM
            // preference. Only subsequent events are genuine user selections.
            if (!localeSelectInitFiredRef.current) {
                localeSelectInitFiredRef.current = true;
                return;
            }

            dispatch(saveLocalePreference(locale));
        };

        document.addEventListener("s-localeselect", handleLocaleSelect);

        return () => {
            document.removeEventListener("s-localeselect", handleLocaleSelect);
        };
    }, [dispatch]);

    const getCurrentNavRef = useCallback((): HTMLElement | null => {
        return isSideNavEnabled ? sideNavRef.current : superNavRef.current;
    }, [isSideNavEnabled]);

    useEffect(() => {
        if (
            getLazyConfig().appEnv !== "local" &&
            session.isAuthenticated &&
            !isAviatorCertificateView
        ) {
            zoominWidget({ zoomInWidgetId17, zoomInWidgetId18, zoomInWidgetId23 });
        }
    }, [
        isAviatorCertificateView,
        session.isAuthenticated,
        zoomInWidgetId17,
        zoomInWidgetId18,
        zoomInWidgetId23,
    ]);

    const tokenProvider = useTokenProvider();

    useEffect(() => {
        if (!isOneWebDomainRuntime()) {
            const ipuaPanel = document.querySelector("ipua-panel") as HTMLElement & {
                getaccesstoken?: TokenProviderFunction;
            };
            if (ipuaPanel && session.hasToken) {
                ipuaPanel.getaccesstoken = tokenProvider;
            }
        }
        dispatch(getSessionAsync());
        // tokenProvider is intentionally excluded from deps - it's a stable reference that only
        // changes when activeCompany.id changes, and including it would cause unnecessary re-runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, session.hasToken]);

    const handleNavigation = useCallback(
        (nativeMouseEvent: MouseEvent, url: string): void => {
            const uri = new URL(url);
            // Legacy standalone: hostname contains "exemptions" (e.g. qa.exemptions.avalara.io)
            // AvaApp: same origin with path starting under the app basename (/exemptions/...)
            const avaAppBasename = `/${AVA_APP_NAME}`;
            const isLegacyInternalNav = uri.hostname.includes("exemptions");
            const isAvaAppInternalNav =
                uri.origin === window.location.origin && uri.pathname.startsWith(avaAppBasename);

            if (!isLegacyInternalNav && !isAvaAppInternalNav) return;

            nativeMouseEvent.preventDefault();
            errorBoundaryRef.current?.removeError?.();

            const isNavigatingToSettings =
                uri.pathname.includes("settings") && !window.location.pathname.includes("settings");
            if (isNavigatingToSettings) {
                const previousPathname = isAvaAppInternalNav
                    ? stripAvaAppBasename(window.location.pathname, avaAppBasename)
                    : window.location.pathname;
                dispatch(setPreviousPath(previousPathname));
            }

            const pathname = isAvaAppInternalNav
                ? stripAvaAppBasename(uri.pathname, avaAppBasename)
                : uri.pathname;
            navigate(`${pathname}${uri.search}`);

            if (!isSideNavEnabled) {
                closeExpandedNavMenu(getCurrentNavRef());
            }
        },
        [dispatch, navigate, isSideNavEnabled, getCurrentNavRef]
    );

    useActiveNavSync(handleNavigation, shouldSelectSettingsInSupernav);

    const navMemo = useMemo(
        () =>
            isSideNavEnabled ? (
                <AuiSidenav
                    // The dataRef is resolved and applied imperatively (see the nav-handler
                    // effect above) rather than driven from React state, since the same effect
                    // already owns this DOM property for the AvaApp shell's nav instance, which
                    // this component never renders and so could never drive via props.
                    active=""
                    requestCurrentAccessToken={tokenProvider}
                    ref={sideNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ) : (
                <AuiSupernav
                    active=""
                    requestCurrentAccessToken={tokenProvider}
                    ref={superNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ),
        [isSideNavEnabled, tokenProvider, handleNavigation]
    );

    const auiSessionTimeoutMemo = useMemo(
        () => <AuiSessionTimeout onS-timeout={() => logout()} />,
        []
    );

    const appDOM: React.JSX.Element | null = getAppDOMContent(
        session,
        isCompanyProvisioned,
        isNoAccessUserFlag,
        currentLocation,
        isSideNavEnabled,
        isLoading,
        hasInitialSessionLoaded,
        session.hasSessionLoadError
    );

    if (isOneWebDomainRuntime()) {
        return <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>;
    }

    return (
        <div
            className="app-container flex dl-flex-fill-height"
            data-testid={testIDs.common.container}>
            {session.isAuthenticated && session.hasToken && !isAviatorCertificateView ? (
                <AuiHeader
                    sideNavHeader={isSideNavEnabled}
                    appKey={header.appKey}
                    user={header.user}
                    logoutUrl={header.logoutUrl}
                    onS-navigate={e => retainCriticalData(e)}
                    integrateHelp={!isRetailView}
                    integrateWaffleMenu={isWaffleMenuEnabled}
                    waffleMenuActiveItem="avalara-portal"
                    integrateLocaleSwitcher={isLocaleSwitcherEnabled}
                    localeSwitcherOptionsList={
                        isLocaleSwitcherEnabled && localeOptions.length > 0
                            ? localeOptions
                            : undefined
                    }
                    requestCurrentAccessToken={tokenProvider}
                    className={
                        isRetailView
                            ? "hide-account-products-usage hide-account-support-cases hide-account-billing hide-account-change-password hide-account-id hide-account-my-downloads"
                            : ""
                    }
                    showChatButton={!isRetailView}
                    showThemeSwitcher={isThemeSwitcherEnabled}
                    data-testid={testIDs.shared.header}
                />
            ) : null}
            {session.isAuthenticated &&
            session.hasToken &&
            isLocaleSwitcherEnabled &&
            !isAviatorCertificateView ? (
                <AuiLanguageSupportDialog
                    currentLocale={activeLocale}
                    localeSwitcherOptionsList={localeOptions.length > 0 ? localeOptions : undefined}
                    data-testid={testIDs.languageSupportDialog.container}
                />
            ) : null}
            <div className={`app-content ${isSideNavEnabled ? "" : "flex dl-flex-fill-height"}`}>
                {session.hasToken && !hideNav && !isRetailView && !isAviatorCertificateView
                    ? navMemo
                    : null}
                <div
                    className={`main-content ${isAviatorCertificateView ? "" : "margin-top-lg"} ${
                        isSideNavEnabled && !isAviatorCertificateView
                            ? "dl-content-sidenav"
                            : getMainContentClassName(
                                  isRetailView,
                                  hideNav || isAviatorCertificateView
                              )
                    }`}>
                    <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>
                </div>
            </div>
            <SToasts role="status" />
            {!isSideNavEnabled && !isAviatorCertificateView ? (
                <AuiFooter user={header.user} />
            ) : null}
            {session.hasToken && !isAviatorCertificateView ? (
                <ipua-panel product="ECM" url={getLazyConfig().identity} />
            ) : null}
            {auiSessionTimeoutMemo}
            <AppVersion />
        </div>
    );
}

export default React.memo(Application);
